import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// ==================================================
// PERSONAL FINANCE ADVISOR — "ACT AS CLIENT" CONTEXT
// ==================================================
// When an advisor selects a client, all existing module pages should display
// (and propose changes to) that client's data without any page changes. We do
// this entirely in the request interceptor:
//   * GET/HEAD on a client-data path  -> attach the X-Client-Id header so the
//     backend serves the client's data.
//   * Mutating requests on a writable module path -> transparently rewrite into
//     a pending change-request (POST /pfa/requests). The advisor never mutates
//     client data directly; the client approves it later.

let _actingClientId =
  (typeof window !== "undefined" &&
    window.localStorage.getItem("pfa_active_client")) ||
  null;

export function setActingClient(clientId) {
  _actingClientId = clientId || null;
  if (typeof window !== "undefined") {
    if (clientId) window.localStorage.setItem("pfa_active_client", clientId);
    else window.localStorage.removeItem("pfa_active_client");
  }
}

export function getActingClient() {
  return _actingClientId;
}

// Endpoints that always act as the *real* user (advisor), never as a client.
const SELF_PATH_PREFIXES = [
  "/auth",
  "/pfa",
  "/connections",
  "/advisor-modules",
  "/advisor-code",
  "/notifications",
  "/admin",
  "/demo",
  "/subscription",
  "/support",
];

// Map a resource path -> the change-request module it belongs to. Only these
// modules support advisor-proposed writes.
const WRITE_MODULE_PREFIXES = [
  ["/assets", "assets"],
  ["/liabilities", "liabilities"],
  ["/insurance", "insurance"],
  ["/goals", "goals"],
];

function stripQuery(url = "") {
  const i = url.indexOf("?");
  return i === -1 ? url : url.slice(0, i);
}

function isSelfPath(path) {
  return SELF_PATH_PREFIXES.some(
    (p) => path === p || path.startsWith(p + "/") || path.startsWith(p + "?"),
  );
}

// Translate a mutating module request into a change-request descriptor, or
// return null if this path isn't a writable module resource.
function asChangeRequest(method, path) {
  const m = (method || "get").toLowerCase();
  if (m === "get" || m === "head" || m === "options") return null;

  for (const [prefix, module] of WRITE_MODULE_PREFIXES) {
    if (path === prefix) {
      if (m === "post") return { module, operation: "create", target_id: null };
      return null;
    }
    if (path.startsWith(prefix + "/")) {
      const rest = path.slice(prefix.length + 1);
      // Only treat /<resource>/<id> as a record op (ignore deeper subpaths).
      if (rest.includes("/")) return null;
      if (m === "put" || m === "patch")
        return { module, operation: "update", target_id: rest };
      if (m === "delete")
        return { module, operation: "delete", target_id: rest };
      return null;
    }
  }
  return null;
}

// A bulk-add (POST /<module>/bulk) → one change request per item.
function asBulkChangeRequest(method, path) {
  if ((method || "get").toLowerCase() !== "post") return null;
  for (const [prefix, module] of WRITE_MODULE_PREFIXES) {
    if (path === prefix + "/bulk") return { module };
  }
  return null;
}

// Marker so callers can detect an advisor write was queued for approval.
export const PFA_REQUEST_QUEUED = "__pfa_change_request_queued__";

api.interceptors.request.use((config) => {
  const clientId = _actingClientId;
  if (!clientId) return config;

  const path = stripQuery(config.url || "");
  if (isSelfPath(path)) return config;

  // Bulk add → fan out into one create request per item for the client to
  // approve. `config.data` is the array of built records.
  const bulk = asBulkChangeRequest(config.method, path);
  if (bulk) {
    const items = Array.isArray(config.data) ? config.data : [];
    config.method = "post";
    config.url = "/pfa/requests/bulk";
    config.data = { client_id: clientId, module: bulk.module, items };
    config.headers = { ...(config.headers || {}) };
    delete config.headers["X-Client-Id"];
    config[PFA_REQUEST_QUEUED] = true;
    return config;
  }

  const cr = asChangeRequest(config.method, path);
  if (cr) {
    // Rewrite into an approval request. The advisor's edit form payload becomes
    // the proposed change; the original page can re-fetch afterwards.
    config.method = "post";
    config.url = "/pfa/requests";
    config.data = {
      client_id: clientId,
      module: cr.module,
      operation: cr.operation,
      target_id: cr.target_id,
      payload: cr.operation === "delete" ? null : config.data || {},
      summary: cr.summary,
    };
    config.headers = { ...(config.headers || {}) };
    delete config.headers["X-Client-Id"];
    config[PFA_REQUEST_QUEUED] = true;
    return config;
  }

  // Read (or non-writable module): serve the client's data.
  config.headers = { ...(config.headers || {}), "X-Client-Id": clientId };
  return config;
});

// ==================================================
// REFRESH STATE
// ==================================================

let isRefreshing = false;

let failedQueue = [];

// ==================================================
// PROCESS QUEUE
// ==================================================

const processQueue = (error = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });

  failedQueue = [];
};

// ==================================================
// RESPONSE INTERCEPTOR
// ==================================================

api.interceptors.response.use(
  (response) => {
    // Surface a clear signal that an advisor write was queued for approval so
    // existing pages can show it without bespoke wiring.
    if (response.config && response.config[PFA_REQUEST_QUEUED]) {
      try {
        window.dispatchEvent(
          new CustomEvent("pfa:request-queued", { detail: response.data }),
        );
      } catch (_) {}
    }
    return response;
  },

  async (error) => {
    const originalRequest = error.config;

    // --------------------------------------------
    // NO RESPONSE
    // --------------------------------------------

    if (!error.response) {
      return Promise.reject(error);
    }

    // --------------------------------------------
    // SKIP AUTH ROUTES
    // --------------------------------------------

    const authUrl = originalRequest.url || "";

    const isAuthRoute =
      authUrl.includes("/auth/login") ||
      authUrl.includes("/auth/register") ||
      authUrl.includes("/auth/oauth") ||
      authUrl.includes("/auth/refresh") ||
      authUrl.includes("/auth/logout");

    if (isAuthRoute) {
      return Promise.reject(error);
    }

    // --------------------------------------------
    // ONLY HANDLE 401
    // --------------------------------------------

    if (error.response.status !== 401) {
      return Promise.reject(error);
    }

    // Don't refresh if already on auth pages

    const currentPath = window.location.pathname;

    const isPublicPage =
      currentPath === "/" ||
      currentPath === "/login" ||
      currentPath === "/register" ||
      currentPath === "/forgot-password";

    if (isPublicPage) {
      return Promise.reject(error);
    }

    // --------------------------------------------
    // PREVENT INFINITE RETRY
    // --------------------------------------------

    if (originalRequest._retry) {
      window.location.href = "/login";

      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // --------------------------------------------
    // IF REFRESH IN PROGRESS
    // WAIT FOR IT
    // --------------------------------------------

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve,
          reject,
        });
      })
        .then(() => {
          return api(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    // --------------------------------------------
    // START REFRESH
    // --------------------------------------------

    isRefreshing = true;

    try {
      await api.post("/auth/refresh");

      processQueue();

      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);

      // Hard logout state

      if (
        window.location.pathname.startsWith("/dashboard") ||
        window.location.pathname.startsWith("/admin")
      ) {
        window.location.href = "/login";
      }

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;

  if (detail == null) {
    return err?.message || "Something went wrong";
  }

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" • ");
  }

  if (typeof detail === "object" && typeof detail.msg === "string") {
    return detail.msg;
  }

  return String(detail);
}

export function fmtINR(n, opts = {}) {
  if (n === null || n === undefined || isNaN(n)) {
    return "₹0";
  }

  const { compact = false, decimals = 0 } = opts;

  if (compact) {
    if (Math.abs(n) >= 1e7) {
      return `₹${(n / 1e7).toFixed(decimals || 2)}Cr`;
    }

    if (Math.abs(n) >= 1e5) {
      return `₹${(n / 1e5).toFixed(decimals || 2)}L`;
    }

    if (Math.abs(n) >= 1e3) {
      return `₹${(n / 1e3).toFixed(decimals || 1)}K`;
    }
  }

  return (
    "₹" +
    Number(n).toLocaleString("en-IN", {
      maximumFractionDigits: decimals,
    })
  );
}

export function fmtPct(n, decimals = 2) {
  if (n === null || n === undefined || isNaN(n)) {
    return "0%";
  }

  return `${Number(n).toFixed(decimals)}%`;
}
